import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
// material
import { Container, Grid, Typography, Card, CardContent, Skeleton, Box } from '@mui/material';
// components
import InfoCard from '../../components/info-card';
import Page from '../../components/Page';
import LineChart from '../../components/charts/line-chart';
import PieChart from '../../components/charts/pie-chart';
import BFTable from '../../components/table';
import InfoPopover from '../../components/info-popover';
import InspectionsCalendar from '../../components/inspections-calendar';
import ControlDetailsPreview from '../../components/inspections-calendar/ControlDetailsPreview';
import ScheduleInspectionDialog from '../../components/schedule-inspection-dialog';
// Redux
import { getCards, getCharts, getRecentControls, getCalendarEvents, getCalendarControllers, updateControllerColor, cancelCalendarEvent, updateEventDate, scheduleInspection } from './actions';
import { selectCards, selectCharts, selectRecentControls } from './selectors';
import { hasPermission } from '../login/selectors';
import { Permissions } from '../../entities/permissions';
import { useQueryState } from '../../utils/query-state';
import generateColumns from './columns';


export default function Dashboard() {
  const { t } = useTranslation()
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const cards = useSelector(selectCards());
  const charts = useSelector(selectCharts());
  const recentControls = useSelector(selectRecentControls());

  // Calendar permissions
  const canSeeAllEvents = useSelector(hasPermission(Permissions.PAGE_DASHBOARD_CALENDAR_SEE_ALL_EVENTS));
  const canRescheduleEvents = useSelector(hasPermission(Permissions.PAGE_DASHBOARD_CALENDAR_RESCHEDULE_EVENTS));
  const canCancelOverdueEvents = useSelector(hasPermission(Permissions.PAGE_DASHBOARD_CALENDAR_CANCEL_OVERDUE_EVENTS));
  const canCreateControl = useSelector(hasPermission(Permissions.PAGE_DASHBOARD_CALENDAR_CREATE_CONTROL));

  // Calendar state persisted in URL
  const [calendarView, setCalendarView] = useQueryState({
    paramName: 'calendarView',
    fromQuery: (value) => value || 'dayGridMonth',
    toQuery: (value) => value,
    defaultValue: 'dayGridMonth',
  });

  const [calendarDate, setCalendarDate] = useQueryState({
    paramName: 'calendarDate',
    fromQuery: (value) => value ? new Date(value) : null,
    toQuery: (value) => value instanceof Date ? value.toISOString().split('T')[0] : value,
    defaultValue: null,
  });

  const [calendarControllers, setCalendarControllers] = useQueryState({
    paramName: 'calendarControllers',
    defaultValue: [],
  });

  // Schedule inspection dialog state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleDialogDate, setScheduleDialogDate] = useState(null);
  const [controllers, setControllers] = useState([]);
  const [loadingControllers, setLoadingControllers] = useState(false);
  const calendarRefreshRef = useRef(null);

  // Calendar functions
  const fetchCalendarEvents = useCallback((params) => dispatch(getCalendarEvents(params)), [dispatch]);

  const fetchControllers = useCallback(() => {
    setLoadingControllers(true);
    return dispatch(getCalendarControllers()).then((result) => {
      if (result && result.controllers) {
        setControllers(result.controllers);
      }
      setLoadingControllers(false);
      return result;
    }).catch(() => {
      setLoadingControllers(false);
    });
  }, [dispatch]);

  const handleUpdateControllerColor = useCallback((userId, color) => dispatch(updateControllerColor(userId, color)), [dispatch]);

  const handleCancelEvent = useCallback((type, controlId) => dispatch(cancelCalendarEvent(type, controlId)), [dispatch]);

  const handleUpdateEventDate = useCallback((type, controlId, newDate) => dispatch(updateEventDate(controlId, newDate)), [dispatch]);

  const handleScheduleInspection = useCallback((data) => dispatch(scheduleInspection(data)), [dispatch]);

  const handleCreateControl = useCallback((date) => {
    setScheduleDialogDate(date);
    setScheduleDialogOpen(true);
  }, []);

  const handleScheduleDialogClose = useCallback(() => {
    setScheduleDialogOpen(false);
    setScheduleDialogDate(null);
  }, []);

  const handleInspectionScheduled = useCallback(() => {
    // Refresh the calendar after scheduling
    if (calendarRefreshRef.current) {
      calendarRefreshRef.current();
    }
  }, []);

  useEffect(() => {
    // Progressive loading: fetch all endpoints in parallel
    dispatch(getCards());
    dispatch(getCharts());
    dispatch(getRecentControls());
  }, [dispatch]);

  // Generate table columns
  const columns = useMemo(() => generateColumns({ t, theme }), [t, theme]);

  // Transform data to match BFTable expected format
  const tableData = useMemo(() => {
    if (recentControls.loading || recentControls.data.length === 0) {
      return null;
    }
    return {
      items: recentControls.data,
      itemCount: recentControls.data.length,
      range: {
        limit: recentControls.data.length,
        offset: 0
      }
    };
  }, [recentControls]);

  const handleRowClick = (rowData, rowMeta) => {
    const control = recentControls.data[rowMeta.dataIndex];
    navigate(`/dashboard/inspections/${control.id}`);
  };

  return (
    <Page title={t('dashboard.tab-title')}>
      <Container maxWidth="xl">
        <Typography variant="h3" sx={{ mb: 5 }}>
          {t('dashboard.page-title')}
        </Typography>

        {/* Cards Section */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {cards.loading ? (
            // Show skeleton loaders while loading
            [1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
              </Grid>
            ))
          ) : (
            cards.data.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={item.label}>
                <InfoCard
                  title={item.label}
                  value={item.count.toString() + (item.suffix || '')}
                  infoMessage={t(`dashboard.cards.info.${index}`)}
                />
              </Grid>
            ))
          )}
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} md={8}>
            <Card sx={{ position: 'relative' }}>
              <CardContent>
                <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
                  <InfoPopover message={t('dashboard.charts.info.controls-over-time')} />
                </Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('dashboard.charts.controls-over-time')}
                </Typography>
                <LineChart
                  title=""
                  noDataMessage={t('dashboard.charts.no-data')}
                  data={charts.controlsOverTime}
                  isLoading={charts.loading}
                  xLabel={t('dashboard.charts.date')}
                  yLabel={t('dashboard.charts.count')}
                  tooltipLabel="{x}: {y}"
                  activeFilters={[]}
                  style={{
                    title: { fontSize: 18, fontWeight: 600 },
                    chart: { padding: { top: 20, bottom: 60, left: 60, right: 50 } },
                    line: { data: { stroke: theme.palette.primary.main, strokeWidth: 1.5 } },
                    tooltip: { fontSize: 9 },
                    xLabel: {
                      axisLabel: { fontSize: 10, padding: 35 },
                      tickLabels: { fontSize: 9 }
                    },
                    yLabel: {
                      axisLabel: { fontSize: 10, padding: 40 },
                      tickLabels: { fontSize: 9 }
                    },
                    noDataMessage: { fontSize: 12 }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ position: 'relative' }}>
              <CardContent>
                <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
                  <InfoPopover message={t('dashboard.charts.info.compliance-distribution')} />
                </Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('dashboard.charts.compliance-distribution')}
                </Typography>
                <PieChart
                  title=""
                  noDataMessage={t('dashboard.charts.no-data')}
                  data={charts.complianceDistribution}
                  isLoading={charts.loading}
                  colorScale={[theme.palette.error.main, theme.palette.success.main, theme.palette.info.main]}
                  tooltipLabel="{x}: {y} ({percentage}%)"
                  enableTooltip
                  activeFilters={[]}
                  style={{
                    title: { fontSize: 18, fontWeight: 600 },
                    pie: { labels: { fontSize: 14 } },
                    tooltip: { fontSize: 14 }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Control Calendar */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12}>
            <InspectionsCalendar
              title={t('dashboard.inspections-calendar.title')}
              fetchEventsFunction={fetchCalendarEvents}
              fetchControllersFunction={fetchControllers}
              updateControllerColorFunction={handleUpdateControllerColor}
              cancelEventFunction={handleCancelEvent}
              updateEventDateFunction={handleUpdateEventDate}
              height="700px"
              itemLabelSingular={t('dashboard.inspections-calendar.resource-singular')}
              itemLabelPlural={t('dashboard.inspections-calendar.resource-plural')}
              showControllerFilter={canSeeAllEvents}
              canRescheduleEvents={canRescheduleEvents}
              canCancelOverdueEvents={canCancelOverdueEvents}
              canCreateControl={canCreateControl}
              defaultView={calendarView}
              onViewChange={setCalendarView}
              defaultDate={calendarDate}
              onDateChange={setCalendarDate}
              defaultSelectedControllers={calendarControllers}
              onSelectedControllersChange={setCalendarControllers}
              onCreateControl={handleCreateControl}
              onRefreshRef={(refreshFn) => { calendarRefreshRef.current = refreshFn; }}
              getEventTitle={(event) => `| ${event.resource?.name || t('calendar.unassigned')}`}
              onControlClick={(event) => {
                // Open control detail page in new tab to preserve calendar flow
                if (event.controlId) {
                  const path = event.type === 'membercontrol'
                    ? `/dashboard/members/control/${event.controlId}`
                    : `/dashboard/inspections/${event.controlId}`;
                  window.open(path, '_blank');
                }
              }}
              exportCSVFunction={(events, { t, selectedDate, isEventPast, isEventCompleted, CONTROL_STATUS, moment }) => {
                const getConformityLabel = (conformity) => {
                  if (conformity === true || conformity === 1) return t('conformity.valid', { defaultValue: 'Valid' });
                  if (conformity === false || conformity === 0) return t('conformity.invalid', { defaultValue: 'Invalid' });
                  return t('conformity.waiting-result', { defaultValue: 'Waiting' });
                };

                const getStatusLabel = (event) => {
                  const isCanceled = event.status === CONTROL_STATUS.CANCELED;
                  if (isCanceled) return t('calendar.status.canceled');
                  const eventDate = event.dateTime || event.date;
                  const isOverdue = isEventPast(eventDate) && !isEventCompleted(event.conformity);
                  if (isOverdue) return t('calendar.status.late');
                  return '';
                };

                const csvContent = [
                  [
                    t('calendar.export.date'),
                    t('calendar.export.controller'),
                    t('calendar.export.resource'),
                    t('calendar.export.resource-type'),
                    t('calendar.export.flow'),
                    t('calendar.export.conformity'),
                    t('calendar.export.status'),
                  ].join(','),
                  ...events.map((event) => {
                    const controllerName = event.controller
                      ? `${event.controller.firstName} ${event.controller.lastName}`
                      : t('calendar.unassigned');
                    const dateTime = event.dateTime
                      ? moment(event.dateTime).format('DD/MM/YYYY HH:mm')
                      : moment(event.date).format('DD/MM/YYYY');
                    const resourceName = event.resource?.name || 'N/A';
                    const resourceType = event.resourceType?.name || 'N/A';
                    const flowName = event.flow?.name || 'N/A';
                    const conformity = getConformityLabel(event.conformity);
                    const status = getStatusLabel(event);
                    return [
                      dateTime,
                      controllerName,
                      resourceName,
                      resourceType,
                      flowName,
                      conformity,
                      status,
                    ]
                      .map((field) => `"${String(field).replace(/"/g, '""')}"`)
                      .join(',');
                  }),
                ].join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `inspections_${moment(selectedDate).format('YYYY-MM-DD')}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              renderControlDetails={(control, { onClose, onViewFullDetails, onCancel, controllerColors, inline, isOverdue }) => (
                <ControlDetailsPreview
                  control={control}
                  onClose={onClose}
                  onViewFullDetails={onViewFullDetails}
                  onCancel={onCancel}
                  controllerColors={controllerColors}
                  inline={inline}
                  isOverdue={isOverdue}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Recent Controls Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('dashboard.recent-controls.title')}
            </Typography>
            {recentControls.loading && (
              <Skeleton variant="rectangular" height={400} />
            )}
            {!recentControls.loading && !tableData && (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                {t('dashboard.recent-controls.no-data')}
              </Typography>
            )}
            {!recentControls.loading && tableData && (
              <BFTable
                id="recent-controls-table"
                columns={columns}
                data={tableData}
                pagination={false}
                onRowClick={handleRowClick}
              />
            )}
          </CardContent>
        </Card>

        {/* Schedule Inspection Dialog */}
        <ScheduleInspectionDialog
          open={scheduleDialogOpen}
          onClose={handleScheduleDialogClose}
          onScheduled={handleInspectionScheduled}
          defaultDate={scheduleDialogDate}
          controllers={controllers}
          loadingControllers={loadingControllers}
          scheduleInspectionFunction={handleScheduleInspection}
        />
      </Container>
    </Page>
  );
}
